import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { formatCalendarDate as fmtDate, formatPaymentDate, operationsDate } from './dates';
import '../salary/SalaryModule.css';

interface PeriodRow {
  period_id: number;
  period_start: string;
  period_end: string;
  payout_date: string;
  hourly_rate: number | string;
  hours: number | string | null;
  gross: number | string | null;
  tax: number | string | null;
  cpp_employee: number | string | null;
  ei_employee: number | string | null;
  net: number | string | null;
  payment_id: number | null;
  paystub_number: string | null;
  paid_at: string | null;
  method: string | null;
}

const fmtCAD = (n: number | string | null) =>
  Number(n || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });

const MyPay: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const [rows, setRows] = useState<PeriodRow[]>([]);
  const [hasProfile, setHasProfile] = useState(true);
  // Cash employees are paid outside the remitted payroll and get no statement.
  const [isCash, setIsCash] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // The pay stub currently shown in the inline viewer (null = table view).
  const [viewer, setViewer] = useState<{ paymentId: number; label: string } | null>(null);
  // period_id of the row whose stub isn't ready yet — the notice renders under that card.
  const [historyYear, setHistoryYear] = useState<string>('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/my-pay', {
          cache: 'no-store',
          headers: { 'X-Session-Token': sessionToken || '' },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        setRows(data.payments || []);
        setHasProfile(data.has_employee_profile !== false);
        setIsCash(data.is_cash_employee === true);
      } catch (e: any) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionToken]);

  const stubUrl = (paymentId: number) =>
    `/api/salary/payments/${paymentId}/paystub?token=${encodeURIComponent(sessionToken || '')}`;

  const onStubClick = (r: PeriodRow) => {
    if (isCash) return;
    if (r.payment_id && r.paystub_number) {
      setViewer({
        paymentId: r.payment_id,
        label: `${fmtDate(r.period_start)} – ${fmtDate(r.period_end)}`,
      });
    }
  };

  const backLink = (
    <a className="sal-back" href="/"><span className="home" aria-hidden>⌂</span> Back to app</a>
  );

  if (loading) {
    return <div className="mypay"><div className="sal-topbar">{backLink}<div className="sal-titles"><h2>My Pay</h2></div></div><p>Loading…</p></div>;
  }

  if (!hasProfile) {
    return (
      <div className="mypay">
        <div className="sal-topbar">{backLink}<div className="sal-titles"><h2>My Pay</h2></div></div>
        <div className="info-banner" style={{ background: '#fff7e6', borderLeft: '4px solid #ff9800', padding: '0.75rem 1rem' }}>
          You don't have a payroll profile yet. Contact your admin to set one up — once it's linked, your pay history will show up here.
        </div>
      </div>
    );
  }

  const today = operationsDate();
  const thisYear = Number(today.slice(0, 4));
  const ytd = rows
    .filter((r) => r.payment_id && r.paid_at && Number(operationsDate(new Date(r.paid_at)).slice(0, 4)) === thisYear)
    .reduce(
      (acc, r) => ({
        gross: acc.gross + Number(r.gross || 0),
        tax: acc.tax + Number(r.tax || 0),
        cpp: acc.cpp + Number(r.cpp_employee || 0),
        ei: acc.ei + Number(r.ei_employee || 0),
        net: acc.net + Number(r.net || 0),
      }),
      { gross: 0, tax: 0, cpp: 0, ei: 0, net: 0 }
    );

  const startDate = rows.length ? [...rows].map(r => r.period_start).sort()[0] : null;
  const latestPayment = [...rows].filter(r => r.paid_at).sort((a, b) => (b.paid_at || '').localeCompare(a.paid_at || ''))[0];
  const nextPayday = [...rows].filter(r => r.payout_date?.slice(0, 10) >= today).sort((a, b) => a.payout_date.localeCompare(b.payout_date))[0];

  return (
    <div className="mypay">
      <div className="sal-topbar">
        {backLink}
        <div className="sal-titles">
          <h2>My Pay</h2>
          <div className="sub">{user?.name ? `${user.name} · ` : ''}{thisYear}</div>
        </div>
      </div>

      {err && <div className="error-banner">{err}</div>}

      <div className="salary-card cs-sticky"><strong>Latest payment: {latestPayment ? formatPaymentDate(latestPayment.paid_at) : 'None recorded'}</strong><p>Next scheduled payday: {nextPayday ? fmtDate(nextPayday.payout_date) : 'Not scheduled yet'}</p></div>
      <div className="sal-hero">
        <div className="toprow">
          <div>
            <div className="l">Net paid year-to-date</div>
            <div className="big num">{fmtCAD(ytd.net)}</div>
          </div>
        </div>
        <div className="split">
          <div><div className="l">Gross</div><div className="v num">{fmtCAD(ytd.gross)}</div></div>
          <div><div className="l">Tax</div><div className="v num">{fmtCAD(ytd.tax)}</div></div>
          <div><div className="l">CPP + EI</div><div className="v num">{fmtCAD(ytd.cpp + ytd.ei)}</div></div>
        </div>
      </div>

      <div className="sal-label">
        Pay periods
        {startDate && <span className="light">from {fmtDate(startDate)} · start date</span>}
      </div>

      <div className="cs-toolbar"><label>History year <select value={historyYear} onChange={e => setHistoryYear(e.target.value)}><option value="all">All years</option>{Array.from(new Set(rows.map(r => r.period_start.slice(0, 4)))).sort().reverse().map(year => <option key={year}>{year}</option>)}</select></label></div>
      {rows.length === 0 ? (
        <p style={{ color: 'var(--cs-ink-2)' }}>No pay periods yet.</p>
      ) : (
        rows.filter(r => historyYear === 'all' || r.period_start.slice(0, 4) === historyYear).map((r) => (
          <React.Fragment key={r.period_id}>
            <div className="sal-row">
              <div className="body">
                <b>{fmtDate(r.period_start)} to {fmtDate(r.period_end)}</b>
                <div className="m num">Due {fmtDate(r.payout_date)} · rate {fmtCAD(r.hourly_rate)}/h</div>
              </div>
              {!isCash && (
                r.payment_id && r.paystub_number ? <button className="sal-minibtn" onClick={() => onStubClick(r)}>View stub</button> : <span className="cs-muted-note">Pay stub not yet available</span>
              )}
            </div>
          </React.Fragment>
        ))
      )}

      {viewer && (
        <div className="sal-viewer" role="dialog" aria-modal="true" aria-label="Pay stub viewer">
          <div className="sal-vbar">
            <b>Pay stub — {viewer.label}</b>
            <div className="sal-vacts">
              <a className="sal-vdl" href={stubUrl(viewer.paymentId)} download>⤓ Download</a>
              <button className="sal-vclose" onClick={() => setViewer(null)} aria-label="Close pay stub and go back">✕ Close</button>
            </div>
          </div>
          <iframe title="Pay stub" src={stubUrl(viewer.paymentId)} />
        </div>
      )}
    </div>
  );
};

export default MyPay;
